// Last-Synced-Time display buckets, shared by the device listing card (US-112439 §4)
// and the device details header (US-112437 §1b).
//
// The AC frames the buckets "relative to the current ET clock", but every bucket is a
// function of *elapsed* time (now - lastSync), and an elapsed duration is the same number
// of seconds in every timezone. So no tz conversion happens here — that keeps this helper
// consistent with ADR-0007 (instants travel as UTC ISO) and free of off-by-one tz bugs.
// Absolute wall-clock rendering (stop-timer target, schedule times) is a separate concern.

/** Bucket boundaries in seconds. A month is 720h (30d) and a year 8760h (365d) per the AC. */
const SECOND = 1;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 720 * HOUR;
const YEAR = 8760 * HOUR;

type Unit = 'min' | 'hour' | 'day' | 'month' | 'year';

/** Singular when the floored value is exactly 1, plural otherwise ("1 min ago" / "2 mins ago"). */
function pluralize(value: number, unit: Unit): string {
  return `${value} ${unit}${value === 1 ? '' : 's'} ago`;
}

/**
 * Format the elapsed time since `lastSyncedAt` into exactly one AC bucket.
 *
 * Buckets are contiguous with an inclusive lower and exclusive upper bound, and values are
 * floored to a whole number — never a decimal.
 *
 * Returns `null` when the device has never synced, so callers omit the field entirely
 * rather than rendering a placeholder (US-112439 §4 "Device that has never synced").
 */
export function formatLastSync(
  lastSyncedAt: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!lastSyncedAt) return null;

  const synced = new Date(lastSyncedAt);
  if (Number.isNaN(synced.getTime())) return null;

  // Clamp future timestamps (clock skew between device and browser) to the "Just Now" bucket
  // rather than emitting a negative count.
  const elapsed = Math.max(0, (now.getTime() - synced.getTime()) / 1000);

  if (elapsed < 30 * SECOND) return 'Just Now';
  if (elapsed < MINUTE) return '30 secs ago';
  if (elapsed < HOUR) return pluralize(Math.floor(elapsed / MINUTE), 'min');
  if (elapsed < DAY) return pluralize(Math.floor(elapsed / HOUR), 'hour');
  if (elapsed < MONTH) return pluralize(Math.floor(elapsed / DAY), 'day');
  if (elapsed < YEAR) return pluralize(Math.floor(elapsed / MONTH), 'month');
  return pluralize(Math.floor(elapsed / YEAR), 'year');
}
